import React, { useState, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFileAlt, FaSearch, FaSync, FaCalendarAlt, FaBriefcase, FaEye, FaInfoCircle, FaCheckCircle
} from "react-icons/fa";

const AppliedJobs = () => {
  const { appliedJobs, searchQuery: globalSearchQuery } = useOutletContext();
  const navigate = useNavigate();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  const statuses = ["Applied", "Assessment Shared", "Interview Scheduled", "Selected", "Rejected", "Resigned"];

  const getStatusStyles = (status) => {
    switch (status) {
      case "Selected": case "Hired":
        return "bg-green-100 text-green-700";
      case "Rejected":
        return "bg-red-100 text-red-700";
      case "Assessment Shared": case "Interview Scheduled":
        return "bg-indigo-100 text-indigo-700";
      case "Resigned":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  const filteredApps = appliedJobs.filter(app => {
    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
      (app.jobId?.role || app.role || "").toLowerCase().includes(combinedQuery) ||
      (app.status || "").toLowerCase().includes(combinedQuery);

    const matchesStatus = statusFilter ? app.status === statusFilter : true;

    let matchesDate = true;
    if (dateFilter) {
      const appDate = new Date(app.createdAt || app.appliedAt).toISOString().split("T")[0];
      matchesDate = appDate === dateFilter;
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const resetFilters = () => {
    setLocalSearchQuery("");
    setStatusFilter("");
    setDateFilter("");
  };

  return (
    <div className="w-full h-full">
      
      {/* ── Header & Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">
          
          <div className="flex items-center gap-4 mr-auto">
             <div className="p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl text-blue-600 shadow-sm border border-blue-100/50">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                   <FaFileAlt size={20} />
                </div>
             </div>
             <div>
                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600">MY APPLICATIONS</h1>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Track your status</div>
             </div>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[150px]">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search roles..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-36 py-1.5 px-3 text-[10px] font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest bg-white"
          >
            <option value="">ALL STATUSES</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {/* Date Filter */}
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-32 py-1.5 px-3 text-[10px] font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest bg-white"
          />

          {/* Reset */}
          {(localSearchQuery || statusFilter || dateFilter) && (
            <button
              onClick={resetFilters}
              className="h-7 px-3 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition uppercase tracking-widest"
            >
              Clear
            </button>
          )}

        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {filteredApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Application</th>
                  <th className="py-3 px-4 text-center font-bold">Applied On</th>
                  <th className="py-3 px-4 text-center font-bold">Current Status</th>
                  <th className="py-3 px-4 text-center font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredApps.map((app) => (
                  <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-center">
                      <div className="text-xs font-bold text-gray-800">{app.jobId?.role || app.role || "N/A"}</div>
                      <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Ref: {app._id.slice(-6).toUpperCase()}</div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-[10px] font-medium text-gray-500">
                        {new Date(app.createdAt || app.appliedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${getStatusStyles(app.status)}`}>
                          {app.status || "Applied"}
                        </span>
                        {(app.interviewStatus === "Invited" || app.interviewStatus === "Rescheduled") && app.candidateInterviewStatus && app.candidateInterviewStatus !== "Pending" && (
                          <span className={`text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${app.candidateInterviewStatus === "Confirmed" ? "bg-indigo-50 text-indigo-600 border border-indigo-100" : "bg-red-50 text-red-600 border border-red-100"}`}>
                            {app.candidateInterviewStatus}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-3">
                        {/* Assessment Actions - Sequential Flow */}
                        {app.jobId && (app.jobId.assessmentId || (app.jobId.assessmentIds && app.jobId.assessmentIds.length > 0)) ? (() => {
                          // Collect all assessment IDs linked to this job (in order)
                          const allAssessmentIds = [];
                          if (app.jobId.assessmentIds && app.jobId.assessmentIds.length > 0) {
                            app.jobId.assessmentIds.forEach(a => {
                              const id = a?._id || a;
                              if (id) allAssessmentIds.push(String(id));
                            });
                          } else if (app.jobId.assessmentId) {
                            const id = app.jobId.assessmentId?._id || app.jobId.assessmentId;
                            if (id) allAssessmentIds.push(String(id));
                          }

                          // Find which ones the candidate has already completed
                          const completedIds = (app.assessmentResults || []).map(r =>
                            String(r.quizId?._id || r.quizId)
                          );

                          // Find only the FIRST pending assessment (sequential)
                          const nextPendingId = allAssessmentIds.find(id => !completedIds.includes(id));
                          const doneCount = completedIds.filter(id => allAssessmentIds.includes(id)).length;
                          const totalCount = allAssessmentIds.length;
                          const allDone = !nextPendingId && totalCount > 0;

                          if (allDone) {
                            return (
                              <div className="flex flex-col items-center">
                                <span className="text-[10px] font-black text-emerald-600 flex items-center gap-1.5 whitespace-nowrap">
                                  <FaCheckCircle className="text-[11px]" /> ALL VERIFIED
                                </span>
                                {doneCount > 1 && (
                                  <span className="text-[9px] font-bold text-gray-400">{doneCount}/{totalCount} tests</span>
                                )}
                              </div>
                            );
                          }

                          if (nextPendingId) {
                            const nextIdx = allAssessmentIds.indexOf(nextPendingId) + 1;
                            return (
                              <div className="flex flex-col items-center gap-0.5">
                                {doneCount > 0 && (
                                  <span className="text-[9px] font-bold text-emerald-500">{doneCount}/{totalCount} Done ✓</span>
                                )}
                                <button
                                  onClick={() => navigate(`/take-assessment/${app.jobId._id}/${app._id}/${nextPendingId}`)}
                                  className="px-0 bg-transparent text-blue-600 text-[10px] font-bold uppercase tracking-widest hover:text-blue-800 transition-all flex items-center gap-1.5 group/test whitespace-nowrap"
                                >
                                  <FaSync size={12} className="group-hover/test:rotate-180 transition-transform duration-500" />
                                  Test {nextIdx}{totalCount > 1 ? ` of ${totalCount}` : ""}
                                </button>
                              </div>
                            );
                          }

                          return null;
                        })() : null}
                        <button
                          onClick={() => navigate(`/jobs/${app.jobId?._id}`)}
                          className="w-9 h-9 bg-transparent text-blue-600 hover:text-blue-800 transition-all active:scale-95 group flex items-center justify-center"
                          title="View Details"
                        >
                          <FaEye className="text-xl transition-transform group-hover:scale-110" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
              <FaFileAlt size={20} />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">No Applications Found</h3>
            <p className="text-[10px] text-gray-400 font-medium">You haven't applied to any jobs matching this criteria.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-6 pb-2">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Recruitment Tracker • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default AppliedJobs;